/**
 * Class Management Service
 *
 * The class-size limit, listing a school's classes, and assigning a teacher to
 * a class (used by the platform admin approvals page). Every other class and
 * roster change goes through schoolRosterService (/school-roster).
 */

import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import type { Class } from '../types/lms';
import type { UserProfile } from '../utils/rbac';
import { toast } from 'react-toastify';
import * as roster from './schoolRosterService';

/**
 * Largest roster a class may have.
 *
 * Exported so the UI can show progress against the same number rather than
 * hard-coding its own and drifting from what the service actually enforces.
 */
export const MAX_CLASS_SIZE = 30;

/**
 * Classes change only through the /school-roster API, which writes a class and
 * its members' user documents in one transaction; firestore.rules no longer
 * lets a browser write class membership.
 */
const STAFF_ROLES = ['school', 'principal', 'partner', 'admin', 'superadmin'];
const isSchoolStaff = (profile: UserProfile) => STAFF_ROLES.includes(profile.role);

/** The class's school: the server needs it named for admins and partners. */
async function schoolOfClass(classId: string): Promise<string | null> {
  try {
    const snap = await getDoc(doc(db, 'classes', classId));
    return snap.exists() ? (snap.data().school_id ?? null) : null;
  } catch {
    return null;
  }
}

function report<T>(result: roster.RosterResult<T>, success: string, failure: string): boolean {
  if (result.ok) toast.success(success);
  else toast.error(result.message || failure);
  return result.ok;
}

/**
 * Assign teacher to class
 * Updates both class.teacher_ids and user.managed_class_ids
 */
export async function assignTeacherToClass(
  profile: UserProfile | null,
  teacherId: string,
  classId: string
): Promise<boolean> {
  if (!profile) {
    toast.error('Authentication required');
    return false;
  }

  if (!isSchoolStaff(profile)) {
    toast.error('Only school administrators, principals, and admins can assign teachers');
    return false;
  }
  const assigned = await roster.changeClassTeachers(classId, { schoolId: await schoolOfClass(classId), add: [teacherId] });
  return report(assigned, 'Teacher assigned to class successfully', 'Failed to assign teacher');
}

/**
 * Get all classes for a school
 */
export async function getSchoolClasses(
  profile: UserProfile | null,
  schoolId: string
): Promise<Class[]> {
  if (!profile) return [];

  // Permission check - school/principal can only get their school's classes
  const mySchoolId = profile.managed_school_id || profile.school_id;
  if ((profile.role === 'school' || profile.role === 'principal') && mySchoolId !== schoolId) {
    return [];
  }

  if (
    profile.role !== 'school' &&
    profile.role !== 'principal' &&
    profile.role !== 'partner' &&
    profile.role !== 'admin' &&
    profile.role !== 'superadmin' &&
    profile.role !== 'teacher'
  ) {
    return [];
  }

  try {
    const classesQuery = query(
      collection(db, 'classes'),
      where('school_id', '==', schoolId)
    );
    const snapshot = await getDocs(classesQuery);
    return (snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as Class[]).filter((c) => c.archived !== true);
  } catch (error) {
    console.error('Error fetching school classes:', error);
    return [];
  }
}
